/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import {
  useListSubConnectionsQuery,
  useListExternalEntitiesQuery,
  useCreateSubConnectionMutation,
  useDeleteSubConnectionMutation,
  useGetUsedInitiativeAppIntegrationQuery,
  useUpdateConnectionExternalIdMutation,
  useListConfigurationOptionsQuery,
  useApplyConfigurationOptionMutation,
} from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { getCurrentInitiativeTree } from '@selectors/initiative';
import { CTAdminBreadcrumbs } from '../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import { BasicAlert } from '@g17eco/molecules/alert';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { INITIATIVE } from '@constants/terminology';
import type { ExternalEntity, IntegrationConfigurationOption, IntegrationConnectionInfo, SubConnection } from '@g17eco/types/app-integration';
import type { InitiativePlain } from '@g17eco/types/initiative';
import { getErrorMessage } from '@utils/error';

interface AddMappingFormProps {
  initiativeId: string;
  code: string;
  entities: ExternalEntity[];
  initiatives: Array<InitiativePlain & { level: number }>;
  existingMappings: SubConnection[];
  rootExternalId?: string;
}

const AddMappingForm = ({ initiativeId, code, entities, initiatives, existingMappings, rootExternalId }: AddMappingFormProps) => {
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedInitiative, setSelectedInitiative] = useState('');
  const [createSubConnection, { isLoading, error }] = useCreateSubConnectionMutation();

  const mappedEntityIds = new Set(existingMappings.map((m) => m.externalId));
  const mappedInitiativeIds = new Set(existingMappings.map((m) => m.initiativeId));
  const availableEntities = entities.filter((e) => !mappedEntityIds.has(e.id) && e.id !== rootExternalId);
  const childInitiatives = initiatives.filter((init) => init._id !== initiativeId);

  const handleSubmit = async () => {
    const entity = entities.find((e) => e.id === selectedEntity);
    if (!selectedEntity || !selectedInitiative || !entity) {
      return;
    }

    const result = await createSubConnection({
      initiativeId,
      code,
      targetInitiativeId: selectedInitiative,
      externalId: entity.id,
      facilityName: entity.name,
    });

    if ('data' in result) {
      setSelectedEntity('');
      setSelectedInitiative('');
    }
  };

  const unmappedInitiatives = childInitiatives.filter((init) => !mappedInitiativeIds.has(init._id));
  const isFullyMapped = availableEntities.length === 0 || unmappedInitiatives.length === 0;

  if (isFullyMapped && existingMappings.length > 0) {
    return (
      <BasicAlert type='success' className='mb-3'>
        All {availableEntities.length === 0 ? 'external entities' : INITIATIVE.PLURAL} have been mapped.
      </BasicAlert>
    );
  }

  return (
    <>
      {error ? (
        <BasicAlert type='danger' className='mb-3'>{getErrorMessage(error)}</BasicAlert>
      ) : null}
      <div className='d-flex gap-3 align-items-end mb-3'>
        <div className='flex-grow-1'>
          <label className='form-label fw-bold'>External Entity</label>
          <select
            className='form-select'
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value=''>Select entity...</option>
            {availableEntities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name} {entity.category ? `(${entity.category})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className='flex-grow-1'>
          <label className='form-label fw-bold'>{INITIATIVE.CAPITALIZED_SINGULAR}</label>
          <select
            className='form-select'
            value={selectedInitiative}
            onChange={(e) => setSelectedInitiative(e.target.value)}
          >
            <option value=''>Select {INITIATIVE.SINGULAR}...</option>
            {childInitiatives.map((init) => {
              const isMapped = mappedInitiativeIds.has(init._id);
              return (
                <option key={init._id} value={init._id} disabled={isMapped}>
                  {'— '.repeat(Math.max(0, init.level - 1))}{init.name}{isMapped ? ' (mapped)' : ''}
                </option>
              );
            })}
          </select>
        </div>
        <button
          className='btn btn-primary'
          onClick={handleSubmit}
          disabled={!selectedEntity || !selectedInitiative || isLoading}
        >
          {isLoading ? 'Adding...' : 'Add Mapping'}
        </button>
      </div>
    </>
  );
};

interface MappingTableProps {
  subConnections: SubConnection[];
  entities: ExternalEntity[];
  initiatives: Array<InitiativePlain & { level: number }>;
  initiativeId: string;
  code: string;
}

const EntityName = ({ externalId, entityMap, facilityName }: { externalId?: string; entityMap: Map<string, string>; facilityName?: string }) => {
  const freshName = entityMap.get(externalId ?? '');
  if (freshName) {
    return <span>{freshName}</span>;
  }

  const displayName = facilityName ?? externalId;
  return (
    <span>
      {displayName}
      <SimpleTooltip text='Entity no longer available — it may have been removed or you no longer have access to it'>
        <i className='text-ThemeWarningMedium fas fa-exclamation-triangle ms-1' />
      </SimpleTooltip>
    </span>
  );
};

const MappingTable = ({ subConnections, entities, initiatives, initiativeId, code }: MappingTableProps) => {
  const [deleteSubConnection, { error: deleteError }] = useDeleteSubConnectionMutation();
  const initiativeMap = new Map(initiatives.map((i) => [i._id, i.name]));
  const entityMap = new Map(entities.map((e) => [e.id, e.name]));

  if (subConnections.length === 0) {
    return <p className='text-muted'>No entity mappings configured yet.</p>;
  }

  return (
    <>
      {deleteError ? (
        <BasicAlert type='danger' className='mb-3'>{getErrorMessage(deleteError)}</BasicAlert>
      ) : null}
      <table className='table table-hover'>
        <thead>
          <tr>
            <th>External Entity</th>
            <th>Mapped {INITIATIVE.CAPITALIZED_SINGULAR}</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subConnections.map((sc) => (
            <tr key={sc._id}>
              <td><EntityName externalId={sc.externalId} entityMap={entityMap} facilityName={sc.data?.facilityName} /></td>
              <td>{initiativeMap.get(sc.initiativeId) ?? sc.initiativeId}</td>
              <td>
                <span className={`badge bg-${sc.status === 'active' ? 'success' : 'secondary'}`}>
                  {sc.status}
                </span>
              </td>
              <td>
                <button
                  className='btn btn-sm btn-outline-danger'
                  onClick={() => deleteSubConnection({ initiativeId, code, taskId: sc._id })}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

interface RootOrganizationMappingProps {
  initiativeId: string;
  code: string;
  entities: ExternalEntity[];
  currentExternalId?: string;
  existingMappings: SubConnection[];
}

const inferRootEntityId = (entities: ExternalEntity[], currentExternalId?: string): string => {
  if (currentExternalId) {
    return currentExternalId;
  }
  const companyEntity = entities.find((e) => e.category === 'company');
  return companyEntity?.id ?? '';
};

const RootOrganizationMapping = ({
  initiativeId,
  code,
  entities,
  currentExternalId,
  existingMappings,
}: RootOrganizationMappingProps) => {
  const [selectedEntity, setSelectedEntity] = useState(() => inferRootEntityId(entities, currentExternalId));
  const [isEditing, setIsEditing] = useState(!currentExternalId);
  const [updateExternalId, { isLoading, error }] = useUpdateConnectionExternalIdMutation();

  const mappedEntityIds = new Set(existingMappings.map((m) => m.externalId));
  const availableEntities = entities.filter((e) => !mappedEntityIds.has(e.id) || e.id === currentExternalId);
  const currentEntity = entities.find((e) => e.id === currentExternalId);

  const handleSave = async () => {
    if (!selectedEntity) {
      return;
    }

    const result = await updateExternalId({ initiativeId, code, externalId: selectedEntity });
    if ('data' in result) {
      setIsEditing(false);
    }
  };

  if (!isEditing && currentExternalId) {
    return (
      <div className='d-flex align-items-center gap-3 mb-4 p-3 bg-light rounded'>
        <div className='flex-fill'>
          <label className='form-label fw-bold mb-0'>Root Organization</label>
          <p className='mb-0'>
            {currentEntity?.name ?? currentExternalId}
            {currentEntity?.category ? ` (${currentEntity.category})` : ''}
          </p>
        </div>
        <button className='btn btn-sm btn-outline-secondary' onClick={() => setIsEditing(true)}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className='mb-4 p-3 border rounded'>
      <label className='form-label fw-bold'>Root Organization External Entity</label>
      <p className='text-muted small mb-2'>
        Select which external entity maps to the root {INITIATIVE.SINGULAR} (organization).
      </p>
      {error ? (
        <BasicAlert type='danger' className='mb-3'>{getErrorMessage(error)}</BasicAlert>
      ) : null}
      <div className='d-flex gap-3 align-items-end'>
        <div className='flex-grow-1'>
          <select
            className='form-select'
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value=''>Select entity...</option>
            {availableEntities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name} {entity.category ? `(${entity.category})` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          className='btn btn-primary'
          onClick={handleSave}
          disabled={!selectedEntity || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
        {currentExternalId ? (
          <button
            className='btn btn-outline-secondary'
            onClick={() => {
              setSelectedEntity(currentExternalId);
              setIsEditing(false);
            }}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ConnectionHeader = ({ provider, connection }: Pick<IntegrationConnectionInfo, 'provider' | 'connection'>) => {
  return (
    <div className='d-flex align-items-center gap-3 mb-4'>
      <img src={provider.logo} width={40} height={40} alt={provider.name} />
      <div>
        <h6 className='mb-0 fw-bold'>{provider.name}</h6>
        <span className='text-muted small'>{connection.name ?? 'Connection'}</span>
      </div>
    </div>
  );
};

interface CustomAttributePickerProps {
  initiativeId: string;
  code: string;
  options: IntegrationConfigurationOption[];
}

const CustomAttributePicker = ({ initiativeId, code, options }: CustomAttributePickerProps) => {
  const [selected, setSelected] = useState('');
  const [apply, { isLoading, error }] = useApplyConfigurationOptionMutation();

  const handleSubmit = async () => {
    if (!selected) {
      return;
    }
    await apply({ initiativeId, code, optionId: selected });
  };

  return (
    <>
      {error ? (
        <BasicAlert type='danger' className='mb-3'>{getErrorMessage(error)}</BasicAlert>
      ) : null}
      <div className='d-flex gap-3 align-items-end mb-3'>
        <div className='flex-grow-1'>
          <label className='form-label' htmlFor='custom-attribute-picker'>
            Choose which custom attribute to map by
          </label>
          <select
            id='custom-attribute-picker'
            className='form-select'
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value=''>Select an attribute...</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <button
          type='button'
          className='btn btn-primary'
          disabled={!selected || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </>
  );
};

export const EntityMappingRoute = () => {
  const { code, initiativeId } = useParams<{ code: string; initiativeId: string }>();
  const isUserStaff = useAppSelector(isStaff);
  const initiativeTree = useAppSelector(getCurrentInitiativeTree);

  const subConnectionsQuery = useListSubConnectionsQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );
  const externalEntitiesQuery = useListExternalEntitiesQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );
  const connectionQuery = useGetUsedInitiativeAppIntegrationQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );
  const configurationOptionsQuery = useListConfigurationOptionsQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );

  const connectionData = connectionQuery.data?.connection?.data as
    | { externalId?: string; mappingAttributeId?: string }
    | undefined;
  const capabilities = connectionQuery.data?.provider.capabilities;
  const usesConfigurationOptions = capabilities?.configurationOptions === true;
  const autoRootMapping = capabilities?.autoRootMapping === true;
  const mappingAttributeId = connectionData?.mappingAttributeId;
  const selectedOptionName = configurationOptionsQuery.data?.find((o) => o.id === mappingAttributeId)?.name;

  const renderMappingContent = (subConnections: SubConnection[]) => (
    <QueryWrapper
      query={externalEntitiesQuery}
      onSuccess={(entities) => (
        <>
          {autoRootMapping ? null : (
            <RootOrganizationMapping
              initiativeId={initiativeId}
              code={code}
              entities={entities}
              currentExternalId={connectionData?.externalId}
              existingMappings={subConnections}
            />
          )}
          <h6 className='fw-bold'>Subsidiary Entity Mappings</h6>
          <AddMappingForm
            initiativeId={initiativeId}
            code={code}
            entities={entities}
            initiatives={initiativeTree}
            existingMappings={subConnections}
            rootExternalId={connectionData?.externalId}
          />
          <MappingTable
            subConnections={subConnections}
            entities={entities}
            initiatives={initiativeTree}
            initiativeId={initiativeId}
            code={code}
          />
        </>
      )}
    />
  );

  if (!isUserStaff) {
    return (
      <Dashboard>
        <DashboardSectionTitle title={<>Entity Mapping <FeatureStability stability='internal' /></>} />
        <DashboardSection>
          <p>This feature is only available to staff users.</p>
        </DashboardSection>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: 'Entity Mapping' },
          ]}
        />
      </div>
      <DashboardSectionTitle title={<>Entity Mapping <FeatureStability stability='internal' /></>} />
      <DashboardSection>
        {connectionQuery.data ? (
          <ConnectionHeader provider={connectionQuery.data.provider} connection={connectionQuery.data.connection} />
        ) : null}
        <p className='text-muted mb-4'>
          Map external provider entities (facilities, {INITIATIVE.PLURAL}) to {INITIATIVE.PLURAL} in your organisation tree.
          This enables data to be pulled at the {INITIATIVE.SINGULAR} level.
        </p>
        {usesConfigurationOptions && mappingAttributeId ? (
          <p className='text-ThemeTextLight mb-2'>
            Mapping by: <strong>{selectedOptionName ?? mappingAttributeId}</strong>
          </p>
        ) : null}
        {usesConfigurationOptions && !mappingAttributeId ? (
          <QueryWrapper
            query={configurationOptionsQuery}
            onNoData={() => (
              <BasicAlert type='warning'>
                This connection has no custom attributes with values to map by. Configure attributes in the provider first.
              </BasicAlert>
            )}
            onSuccess={(options) => (
              <>
                <p className='text-ThemeTextLight mb-3'>
                  Pick which custom attribute you want to map {INITIATIVE.PLURAL} by. All mappings will use this attribute.
                </p>
                <CustomAttributePicker initiativeId={initiativeId} code={code} options={options} />
              </>
            )}
          />
        ) : (
          <QueryWrapper
            query={subConnectionsQuery}
            onNoData={() => renderMappingContent([])}
            onSuccess={(subConnections) => renderMappingContent(subConnections)}
          />
        )}
      </DashboardSection>
    </Dashboard>
  );
};
